import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, TextField, ToggleButton, ToggleButtonGroup, Card, CardContent, Avatar, Modal, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DataTable from 'react-data-table-component';
import { Add, HourglassEmpty, CheckCircle, Visibility, People } from '@mui/icons-material';
import { primaryColor } from '../theme';
import { useLocation, useNavigate } from 'react-router-dom';
import ViewsBarChart from '../components/ViewBarChart';
import { Link } from 'react-router-dom';
import moment from 'moment';
import axios from 'axios';

const ContentApproverDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [contentData, setContentData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filterTargetUser, setFilterTargetUser] = useState([]);
  const [filterContentStatus, setFilterContentStatus] = useState([]);
  const [filterValueChain, setFilterValueChain] = useState([]);
  const [filterPlatform, setFilterPlatform] = useState([]);
  const [uniqueTargetUsers, setUniqueTargetUsers] = useState([]);
  const [uniqueContentStatuses, setUniqueContentStatuses] = useState([]);
  const [uniqueValueChains, setUniqueValueChains] = useState([]);
  const [uniquePlatforms, setUniquePlatforms] = useState([]);
  const [message, setMessage] = useState(location.state?.message || '');
  const [open, setOpen] = useState(false);
  const [newContent, setNewContent] = useState({
    topic: '',
    targetUser: '',
    valueChain: '',
    platform: '',
    content: '',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [stats, setStats] = useState({
    totalContent: 0,
    pendingApproval: 0,
    toBeReviewed: 0,
    totalViews: 0,
  });

  const user = JSON.parse(localStorage.getItem('user')) || { firstName: 'User', role: 'Role' };

  useEffect(() => {
    fetchData();
    fetchUniqueValues();
    fetchStats();
  }, [currentPage, rowsPerPage, searchText, filterTargetUser, filterContentStatus, filterValueChain, filterPlatform]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/contentb`, {
        params: {
          page: currentPage,
          limit: rowsPerPage,
          search: searchText,
          targetUser: filterTargetUser.join(','),
          contentStatus: filterContentStatus.join(','),
          valueChain: filterValueChain.join(','),
          platform: filterPlatform.join(','),
        },
      });
      setTotalRows(response.data.total);
      setContentData(response.data.content);

      const activityResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/recent-activity`);
      setRecentActivity(activityResponse.data);

    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchUniqueValues = async () => {
    try {
      const [targetUsersResponse, contentStatusesResponse, valueChainsResponse, platformsResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/unique-target-users`),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/unique-content-statuses`),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/unique-value-chains`),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/unique-platforms`),
      ]);

      setUniqueTargetUsers(targetUsersResponse.data);
      setUniqueContentStatuses(contentStatusesResponse.data);
      setUniqueValueChains(valueChainsResponse.data);
      setUniquePlatforms(platformsResponse.data);
    } catch (error) {
      console.error('Error fetching unique values:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/content-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage('');
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleTargetUserChange = (event, newTargetUsers) => {
    setFilterTargetUser(newTargetUsers);
  };

  const handleContentStatusChange = (event, newContentStatus) => {
    setFilterContentStatus(newContentStatus);
  };

  const handlePlatformChange = (event, newPlatforms) => {
    setFilterPlatform(newPlatforms);
  };

  const handleValueChainChange = (event, newValueChains) => {
    setFilterValueChain(newValueChains);
  };
   
  const handleRowClick = (row) => {
    navigate(`/content/${row.id}/review`);
  };

  const applyFilters = (data) => {
    return data.filter(item => 
      (filterTargetUser.length === 0 || filterTargetUser.includes(item.targetUser)) &&
      (filterContentStatus.length === 0 || filterContentStatus.includes(item.status)) &&
      (filterValueChain.length === 0 || filterValueChain.includes(item.valueChain)) &&
      (filterPlatform.length === 0 || filterPlatform.includes(item.platform)) &&
      (item.topic.toLowerCase().includes(searchText.toLowerCase()) ||
       item.valueChain.toLowerCase().includes(searchText.toLowerCase()) ||
       item.status.toLowerCase().includes(searchText.toLowerCase()))
    );
  };

  const filteredContentData = applyFilters(contentData);

  // Prepare data for the bar chart
  const viewsData = filteredContentData.map(content => ({
    date: moment(content.dateUploaded).format('YYYY-MM-DD'),
    views: content.views,
  }));

  const columns = [
    { name: 'Index', selector: (row, index) => index + 1, width: '60px' },
    { name: 'Topic', selector: row => row.topic, sortable: true },
    { name: 'Value Chain', selector: row => row.valueChain, sortable: true },
    { name: 'Status', selector: row => (
      <span style={{
        color: row.status === 'Approved' ? '#28a745' :
               row.status === 'Pending Approval' ? '#ffc107' :
               row.status === 'Review' ? '#dc3545' : 'inherit',
        fontWeight: 'bold'
      }}>
        {row.status}
      </span>
    ), sortable: true },
    { name: 'Date Uploaded', selector: row => moment(row.dateUploaded).format('YYYY-MM-DD'), sortable: true },
    { name: 'Approval Date', selector: row => row.approvalDate ? moment(row.approvalDate).format('YYYY-MM-DD') : 'N/A', sortable: true },
    { name: 'Views', selector: row => row.views, sortable: true },
    { name: 'Downloads', selector: row => row.downloads, sortable: true },
  ];

  const getTimeAgo = (date) => {
    return moment(date).fromNow();
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'content':
        return <Add />;
      case 'review':
        return <CheckCircle />;
      default:
        return <People />;
    }
  };

  return (
    <Container maxWidth="false" sx={{ mt: 4, display: 'flex' }}>
      <Box sx={{ display: { xs: 'none', md: 'block' }, width: '20%', mr: 4 }}>
        <Typography variant="h6" gutterBottom style={{ color: primaryColor }}>
          Filter By
        </Typography>
        <Box mb={1}>
          <Typography variant="subtitle1" style={{ backgroundColor: primaryColor, color: 'white', padding: '8px', borderRadius: '4px' }}>
            Target User
          </Typography>
          <ToggleButtonGroup
            value={filterTargetUser}
            onChange={handleTargetUserChange}
            aria-label="target user"
            orientation="vertical"
            fullWidth
          >
            {uniqueTargetUsers.map(user => (
              <ToggleButton key={user} value={user} aria-label={user} sx={{ borderColor: primaryColor, color: primaryColor }}>
                {user}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box mb={1}>
          <Typography variant="subtitle1" style={{ backgroundColor: primaryColor, color: 'white', padding: '8px', borderRadius: '4px' }}>
            Content Status
          </Typography>
          <ToggleButtonGroup
            value={filterContentStatus}
            onChange={handleContentStatusChange}
            aria-label="content status"
            orientation="vertical"
            fullWidth
          >
            {uniqueContentStatuses.map(status => (
              <ToggleButton key={status} value={status} aria-label={status} sx={{ borderColor: primaryColor, color: primaryColor }}>
                {status}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box mb={1}>
          <Typography variant="subtitle1" style={{ backgroundColor: primaryColor, color: 'white', padding: '8px', borderRadius: '4px' }}>
            Platform
          </Typography>
          <ToggleButtonGroup
            value={filterPlatform}
            onChange={handlePlatformChange}
            aria-label="platform"
            orientation="vertical"
            fullWidth
          >
            {uniquePlatforms.map(platform => (
            <ToggleButton key={platform} value={platform} aria-label={platform} sx={{ borderColor: primaryColor, color: primaryColor }}>
              {platform}
            </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box mb={1}>
          <Typography variant="subtitle1" style={{ backgroundColor: primaryColor, color: 'white', padding: '8px', borderRadius: '4px' }}>
            Value Chain
          </Typography>
          <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
          <ToggleButtonGroup
            value={filterValueChain}
            onChange={handleValueChainChange}
            aria-label="value chain"
            orientation="vertical"
            fullWidth
          >
            {uniqueValueChains.map(chain => (
              <ToggleButton key={chain} value={chain} aria-label={chain} sx={{ borderColor: primaryColor, color: primaryColor }}>
                {chain}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: { xs: 'none', md: 'block' }, width: '60%', ml: 4 }}>
      <Box sx={{ width: '100%', mb: 2 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            color: primaryColor,
            backgroundColor: '#f5f5f5',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          Hello, <span style={{ fontSize: '24px' }}>Welcome to <span style={{ fontWeight: 'bold' }}>{user.role}</span> Dashboard!</span>
        </Typography>
      </Box>
        <Box display="flex" flexWrap="wrap" mb={4}>
          <Box sx={{ flex: '1 1 25%', p: 1 }}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: primaryColor }}>Total Content</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.totalContent}</Typography>
                  </Box>
                  <Add fontSize="large" sx={{ color: primaryColor }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 25%', p: 1 }}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: primaryColor }}>Pending Approval</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.pendingApproval}</Typography>
                  </Box>
                  <HourglassEmpty fontSize="large" sx={{ color: primaryColor }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 25%', p: 1 }}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: primaryColor }}>To be Reviewed</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.toBeReviewed}</Typography>
                  </Box>
                  <CheckCircle fontSize="large" sx={{ color: primaryColor }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 25%', p: 1 }}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: primaryColor }}>Total Views</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.totalViews}</Typography>
                  </Box>
                  <Box sx={{ height: 150, width: '50%' }}>
                    <ViewsBarChart data={viewsData} color={primaryColor} />
                  </Box>
                  <Visibility fontSize="large" sx={{ color: primaryColor }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
        
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <TextField
            label="Search"
            variant="outlined"
            value={searchText}
            onChange={handleSearchChange}
            sx={{ width: '70%' }}
          />
        </Box>
        <DataTable
          columns={columns}
          data={contentData}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          highlightOnHover
          pointerOnHover
          responsive
          onRowClicked={handleRowClick}
          customStyles={{
            headCells: {
              style: {
                backgroundColor: primaryColor,
                color: '#fff',
                fontSize: '16px',
                fontWeight: 'bold',
              },
            },
            cells: {
              style: {
                borderBottom: '1px solid rgba(224, 224, 224, 1)',
              },
            },
          }}
        />
      </Box>
      
      <Box sx={{ display: { xs: 'none', md: 'block' }, width: '20%', ml: 4 }}>
      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}
      <Typography variant="h5" gutterBottom style={{ color: primaryColor }}>
          Recent Activity
        </Typography>
        <Box sx={{ position: 'relative' }}>
          {recentActivity.map((activity, index) => (
            <Box key={activity.id} display="flex" alignItems="center" mb={2} position="relative">
              <Avatar sx={{ bgcolor: primaryColor, mr: 2 }}>
                {getActivityIcon(activity.type)}
              </Avatar>
              <Box>
                <Box display="flex" alignItems="center" mb={1} gap={1}>
                  <Typography variant="body1">{activity.User.firstName} ({activity.User.role})</Typography>
                  <Typography variant="caption" color="textSecondary">{getTimeAgo(activity.date)}</Typography>
                </Box>
                <Typography variant="body2">{activity.activity}</Typography>
              </Box>
              {index < recentActivity.length - 1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: '20px',
                    top: 'calc(60% + 20px)',
                    bottom: '-20px',
                    width: '2px',
                    bgcolor: primaryColor,
                  }}
                />
              )}
            </Box>
          ))}
          <Link to="/logs" style={{ textDecoration: 'none', color: primaryColor }}>
            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
              View More
            </Typography>
          </Link>
        </Box>
      </Box>
    </Container>
  );
};

export default ContentApproverDashboard;